import React, { useState } from 'react';
import { 
    Paper, TableRow, TableCell, Checkbox, Chip, Typography, Button,
    Box, FormControl, Select, MenuItem, Autocomplete, TextField, Tooltip, IconButton, FormControlLabel, Stack, Divider
} from '@mui/material';
import { 
    Sort as SortIcon, DoneAll as DoneAllIcon, RemoveDone as RemoveDoneIcon, MailOutline as MailOutlineIcon 
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, addDays } from 'date-fns';
import DataTable from '../DataTable';
import CustomDatePicker from '../CustomDatePicker';
import { formatDateDisplay } from '../../utils/dateUtils';
import ConfirmDialog from '../ConfirmDialog';
import * as API from '../../api'; 
import HotelOrderDialog from './HotelOrderDialog';

// --- HẰNG SỐ ---
const HOTEL_OPS_LABELS = { 'OPEN': 'Chờ ĐH phòng', 'CONTACTED': 'Đã báo KS', 'CONFIRMED': 'Đã xác nhận', 'DONE': 'Hoàn tất' };
const HOTEL_OPS_COLORS = { 'OPEN': 'error', 'CONTACTED': 'warning', 'CONFIRMED': 'info', 'DONE': 'success' };
const DEFAULT_HOTEL_STATUS = ['OPEN', 'CONTACTED', 'CONFIRMED']; // Mặc định ẩn các phòng đã hoàn tất

const CustomLabel = ({ title }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title}
    </Typography>
);

const HotelOpsTab = ({ bookings, handleViewBooking, onReload, showToast }) => {
    
    // --- 1. STATE BỘ LỌC (GIAO DIỆN) ---
    const [quickFilterMode, setQuickFilterMode] = useState('CUSTOM');
    const [filterStartDate, setFilterStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [filterEndDate, setFilterEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [isAllTime, setIsAllTime] = useState(false);
    const [filterOpsStatus, setFilterOpsStatus] = useState(DEFAULT_HOTEL_STATUS);
    const [filterHotelSupplier, setFilterHotelSupplier] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');

    // --- 2. STATE ACTIVE FILTER (ÁP DỤNG LỌC) ---
    const [activeFilters, setActiveFilters] = useState({
        startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
        opsStatus: DEFAULT_HOTEL_STATUS,
        allTime: false,
        sort: 'asc',
        hotelSupplier: null
    });

    // --- 3. STATE THAO TÁC HÀNG LOẠT & DIALOG ---
    const [selectedIds, setSelectedIds] = useState([]); 
    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', content: '', action: null, confirmColor: 'primary' });

    const [isHotelOrderOpen, setIsHotelOrderOpen] = useState(false);
    const [selectedRoomsForOrder, setSelectedRoomsForOrder] = useState([]);

    // --- HÀM XỬ LÝ SỰ KIỆN GIAO DIỆN BỘ LỌC ---
    const handleQuickFilterChange = (event) => {
        const mode = event.target.value;
        setQuickFilterMode(mode);
        if (mode === 'CUSTOM') return;
        const today = new Date(); let start, end;
        if (mode === 'TODAY') { start = format(today, 'yyyy-MM-dd'); end = format(today, 'yyyy-MM-dd'); }
        else if (mode === 'TOMORROW') { const tmr = addDays(today, 1); start = format(tmr, 'yyyy-MM-dd'); end = format(tmr, 'yyyy-MM-dd'); }
        else if (mode === 'NEXT_3_DAYS') { start = format(today, 'yyyy-MM-dd'); end = format(addDays(today, 3), 'yyyy-MM-dd'); }
        setFilterStartDate(start); setFilterEndDate(end); setIsAllTime(false);
    };

    const handleApplyFilter = () => {
        setActiveFilters({
            startDate: filterStartDate, endDate: filterEndDate, opsStatus: filterOpsStatus,
            allTime: isAllTime, sort: sortOrder, hotelSupplier: filterHotelSupplier
        });
        setSelectedIds([]); 
    };

    const toggleSort = () => {
        const newSort = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(newSort);
        setActiveFilters(prev => ({ ...prev, sort: newSort }));
    };

    const handleToggleAllOpsStatus = () => {
        const allKeys = Object.keys(HOTEL_OPS_LABELS);
        if (filterOpsStatus.length === allKeys.length) setFilterOpsStatus([]);
        else setFilterOpsStatus(allKeys);
    };

    const getHotelSupplierOptions = () => {
        const suppliersMap = new Map();
        bookings.forEach(bk => {
            if (bk.rooms) {
                bk.rooms.forEach(r => {
                    if (r.payment_method === 'SEPARATE' && r.supplier) {
                        const id = typeof r.supplier === 'object' ? r.supplier.id : r.supplier;
                        const name = r.supplier_name || 'Khách sạn / Resort';
                        if (!suppliersMap.has(id)) suppliersMap.set(id, { id, name });
                    }
                });
            }
        });
        return Array.from(suppliersMap.values());
    };

    // --- LOGIC LỌC & TRẢI PHẲNG DỮ LIỆU ---
    const getFilteredRooms = () => {
        let allRooms = [];
        bookings.forEach(bk => {
            if (bk.rooms && bk.rooms.length > 0) {
                bk.rooms.forEach(room => {
                    if (room.payment_method === 'SEPARATE') {
                        allRooms.push({
                            ...room,
                            bk_code: bk.booking_code,
                            customer_detail: bk.customer_detail
                        });
                    }
                });
            }
        });

        return allRooms.filter(room => {
            const isMissingDate = !room.check_in;

            // 1. Lọc thời gian (Bỏ qua nếu chưa cài ngày check-in)
            if (!activeFilters.allTime && !isMissingDate) {
                const dateToCheck = room.check_in.split('T')[0];
                if (dateToCheck < activeFilters.startDate || dateToCheck > activeFilters.endDate) return false;
            }

            // 2. Lọc trạng thái điều hành
            const opsStt = room.operation_status || 'OPEN';
            if (activeFilters.opsStatus.length > 0 && !activeFilters.opsStatus.includes(opsStt)) return false;

            // 3. Lọc theo Khách sạn
            if (activeFilters.hotelSupplier) {
                const sId = typeof room.supplier === 'object' ? room.supplier?.id : room.supplier;
                if (sId !== activeFilters.hotelSupplier.id) return false;
            }

            return true;
        }).sort((a, b) => {
            // Sắp xếp: Phòng chưa xếp lịch lên đầu, sau đó sắp xếp theo ngày check-in
            const noDateA = !a.check_in;
            const noDateB = !b.check_in;
            if (noDateA && !noDateB) return -1;
            if (!noDateA && noDateB) return 1;

            const timeA = a.check_in ? new Date(a.check_in).getTime() : 0;
            const timeB = b.check_in ? new Date(b.check_in).getTime() : 0;
            return activeFilters.sort === 'asc' ? timeA - timeB : timeB - timeA;
        });
    };

    const displayData = getFilteredRooms();

    // --- THAO TÁC BẢNG & HÀNG LOẠT ---
    const handleSelectRow = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedIds(displayData.map(row => row.id));
        else setSelectedIds([]);
    };

    const closeConfirmModal = () => setConfirmModal({ ...confirmModal, open: false });
    const handleConfirmAction = () => {
        if (confirmModal.action) confirmModal.action();
        closeConfirmModal();
    };

    const executeBulkUpdate = async (newStatus) => {
        try {
            // Backend cần có API: PATCH /api/booking-rooms/{id}/update_status/
            const updatePromises = selectedIds.map(id => 
                API.client.patch(`/booking-rooms/${id}/update_status/`, { operation_status: newStatus })
            );
            await Promise.all(updatePromises);

            if (showToast) showToast("Cập nhật trạng thái Phòng thành công!", "success");
            setSelectedIds([]); 
            if (onReload) onReload(); 
        } catch (error) {
            console.error("Lỗi cập nhật:", error);
            if (showToast) showToast("Có lỗi xảy ra khi cập nhật trạng thái.", "error");
        }
    };

    const handleBulkUpdateStatus = (newStatus) => {
        setConfirmModal({
            open: true,
            title: 'Xác nhận cập nhật',
            content: `Cập nhật trạng thái ${HOTEL_OPS_LABELS[newStatus]} cho ${selectedIds.length} phòng đang chọn?`,
            confirmColor: HOTEL_OPS_COLORS[newStatus] || 'primary',
            action: () => executeBulkUpdate(newStatus)
        });
    };

    const handleOpenBulkHotelOrder = () => {
        const selectedObjs = displayData.filter(r => selectedIds.includes(r.id));
        const firstSupplier = selectedObjs[0]?.supplier;
        
        const isSameSupplier = selectedObjs.every(r => {
            const s1 = typeof r.supplier === 'object' ? r.supplier?.id : r.supplier;
            const s2 = typeof firstSupplier === 'object' ? firstSupplier?.id : firstSupplier;
            return s1 === s2;
        });

        if (!isSameSupplier) {
            if(showToast) showToast("Lỗi: Không thể gửi lệnh gộp cho các phòng của Khách sạn KHÁC NHAU!", "error");
            return;
        }
        if (!firstSupplier) {
            if(showToast) showToast("Lỗi: Các phòng được chọn chưa được gán Khách sạn!", "error");
            return;
        }
        
        setSelectedRoomsForOrder(selectedObjs);
        setIsHotelOrderOpen(true);
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            
            {/* === KHU VỰC BỘ LỌC === */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', mb: 2 }}>
                    <FormControl size="small" sx={{ width: '160px', pt: 3.2 }}>
                        <Select value={quickFilterMode} onChange={handleQuickFilterChange} displayEmpty>
                            <MenuItem value="CUSTOM">-- Lọc nhanh --</MenuItem>
                            <MenuItem value="TODAY">Hôm nay</MenuItem>
                            <MenuItem value="TOMORROW">Ngày mai</MenuItem>
                            <MenuItem value="NEXT_3_DAYS">3 ngày tới</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box style={{ width: '160px' }}>
                            <CustomLabel title="Từ ngày Check-in" />
                            <CustomDatePicker value={filterStartDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterStartDate(e.target.value)} />
                        </Box>
                        <Box style={{ width: '160px' }}>
                            <CustomLabel title="Đến ngày Check-in" />
                            <CustomDatePicker value={filterEndDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterEndDate(e.target.value)} />
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Lọc theo Khách sạn / Resort" />
                        <Autocomplete 
                            size="small" fullWidth 
                            options={getHotelSupplierOptions()} getOptionLabel={(option) => option.name || ''} 
                            value={filterHotelSupplier} onChange={(e, newValue) => setFilterHotelSupplier(newValue)} 
                            renderInput={(params) => <TextField {...params} placeholder="Chọn khách sạn..." />} 
                        />
                    </Box>
                    <Box sx={{ pt: 3.2 }}>
                        <Tooltip title={`Sắp xếp ngày nhận phòng: ${sortOrder === 'asc' ? 'Tăng dần' : 'Giảm dần'}`}>
                            <IconButton onClick={toggleSort} sx={{ border: '1px solid #ced4da', borderRadius: 1, height: '40px', color: '#007373', bgcolor: 'white', marginBottom: '12px' }}>
                                <SortIcon sx={{ transform: sortOrder === 'desc' ? 'scaleY(-1)' : 'none' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ pt: 3.2 }}>
                        <Button variant="contained" onClick={handleApplyFilter} sx={{ bgcolor: '#007373', '&:hover': { bgcolor: '#005c5c' }, height: '40px', px: 3, borderRadius: 2, whiteSpace: 'nowrap', marginBottom: '12px' }}>
                            Áp dụng bộ lọc
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box>
                        <FormControlLabel 
                            control={<Checkbox checked={isAllTime} onChange={(e) => { setIsAllTime(e.target.checked); if(e.target.checked) setQuickFilterMode('CUSTOM'); }} sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />} 
                            label={<Typography variant="body2" fontWeight="bold">Toàn thời gian</Typography>} 
                            sx={{ m: 0, mt: 2.5 }} 
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: '300px', borderLeft: '1px solid #eee', pl: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CustomLabel title="Trạng thái ĐH Phòng" />
                            <Tooltip title={filterOpsStatus.length === Object.keys(HOTEL_OPS_LABELS).length ? "Bỏ chọn tất cả" : "Chọn tất cả"}>
                                <IconButton size="small" onClick={handleToggleAllOpsStatus} sx={{ padding: 0, color: '#007373' }}>
                                    {filterOpsStatus.length === Object.keys(HOTEL_OPS_LABELS).length ? <RemoveDoneIcon fontSize="small" /> : <DoneAllIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {Object.keys(HOTEL_OPS_LABELS).map((key) => (
                                <FormControlLabel
                                    key={key}
                                    control={<Checkbox checked={filterOpsStatus.indexOf(key) > -1} onChange={(e) => setFilterOpsStatus(prev => e.target.checked ? [...prev, key] : prev.filter(k => k !== key))} size="small" sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />}
                                    label={<Typography variant="body2">{HOTEL_OPS_LABELS[key]}</Typography>}
                                    sx={{ mr: 0, ml: 0 }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* === THANH BULK ACTION CỦA KHÁCH SẠN === */}
            {selectedIds.length > 0 && (
                <Paper sx={{ p: 1, mb: 2, bgcolor: '#e3f2fd', border: '1px solid #1976d2', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', ml: 2, color: '#1976d2' }}>
                        Đang chọn: {selectedIds.length} phòng
                    </Typography>
                    
                    <Button 
                        variant="contained" size="small" color="primary" 
                        onClick={handleOpenBulkHotelOrder} startIcon={<MailOutlineIcon />}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Tạo lệnh đặt phòng / Gửi Mail
                    </Button>

                    <Divider orientation="vertical" flexItem />

                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Cập nhật trạng thái ĐH:</Typography>
                    <Stack direction="row" spacing={1}>
                        {['CONTACTED', 'CONFIRMED', 'DONE'].map(status => (
                            <Button 
                                key={status} size="small" variant="contained" 
                                sx={{ 
                                    bgcolor: 'white', color: theme => theme.palette[HOTEL_OPS_COLORS[status]].main,
                                    border: '1px solid', borderColor: theme => theme.palette[HOTEL_OPS_COLORS[status]].main,
                                    '&:hover': { bgcolor: theme => theme.palette[HOTEL_OPS_COLORS[status]].main, color: 'white' }
                                }}
                                onClick={() => handleBulkUpdateStatus(status)} 
                            >
                                {HOTEL_OPS_LABELS[status]}
                            </Button>
                        ))}
                    </Stack>
                </Paper>
            )}

            {/* === BẢNG DỮ LIỆU === */}
            <DataTable
                maxHeight="65vh"
                columns={[
                    {
                        label: (
                            <Checkbox color="primary" sx={{ p: 0, color: 'white' }}
                                indeterminate={selectedIds.length > 0 && selectedIds.length < displayData.length}
                                checked={displayData.length > 0 && selectedIds.length === displayData.length}
                                onChange={handleSelectAll}
                            />
                        ),
                        minWidth: 50, align: 'center'
                    },
                    { label: 'Mã BK', minWidth: 100 },
                    { label: 'Khách hàng', minWidth: 150 },
                    { label: 'Khách sạn (NCC)', minWidth: 200 },
                    { label: 'Hạng phòng', minWidth: 180 },
                    { label: 'SL', minWidth: 60, align: 'center' },
                    { label: 'Check-in / Check-out', minWidth: 180 },
                    { label: 'Trạng thái ĐH', minWidth: 120 },
                    { label: 'Thao tác', align: 'center', minWidth: 100 },
                ]}
                data={displayData}
                renderRow={(row) => (
                    <TableRow key={row.id} hover>
                        <TableCell padding="checkbox" align="center">
                            <Checkbox color="primary" checked={selectedIds.includes(row.id)} onChange={(e) => { e.stopPropagation(); handleSelectRow(row.id); }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#007373' }}>{row.bk_code}</TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="bold">{row.customer_detail?.name || 'Khách lẻ'}</Typography>
                            <Typography variant="caption" color="textSecondary">{row.customer_detail?.phone || ''}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                                {row.supplier_name || 'Chưa gán Khách sạn'}
                            </Typography>
                        </TableCell>
                        <TableCell>{row.room_type}</TableCell>
                        <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">{row.quantity}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                <Typography component="span" sx={{ color: '#2e7d32', fontWeight: 'bold', fontSize: '0.8rem' }}>IN:</Typography> {row.check_in ? formatDateDisplay(row.check_in) : '---'}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                <Typography component="span" sx={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '0.8rem' }}>OUT:</Typography> {row.check_out ? formatDateDisplay(row.check_out) : '---'}
                            </Typography>
                            {row.nights_count > 0 && (
                                <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#555' }}>({row.nights_count} đêm)</Typography>
                            )}
                        </TableCell>
                        <TableCell>
                            <Chip 
                                label={HOTEL_OPS_LABELS[row.operation_status || 'OPEN']} 
                                color={HOTEL_OPS_COLORS[row.operation_status || 'OPEN']} 
                                size="small"
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Button size="small" variant="outlined" color="warning" sx={{ fontSize: '0.7rem', py: 0.5, textTransform: 'none', bgcolor: 'white' }} onClick={() => handleViewBooking(row.bk_code)}>
                                Xem BK
                            </Button>
                        </TableCell>
                    </TableRow>
                )}
            />
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#007373' }}>
                    Tổng: {displayData.length} hạng phòng phát sinh lẻ
                </Typography>
            </Box>

            {/* Dialog xác nhận trạng thái */}
            <ConfirmDialog 
                open={confirmModal.open}
                title={confirmModal.title}
                content={confirmModal.content}
                onClose={closeConfirmModal}
                onConfirm={handleConfirmAction}
                confirmColor={confirmModal.confirmColor}
            />

            <HotelOrderDialog 
                open={isHotelOrderOpen}
                onClose={() => setIsHotelOrderOpen(false)}
                selectedRooms={selectedRoomsForOrder}
                showToast={showToast}
                onSuccess={() => { setSelectedIds([]); if(onReload) onReload(); }}
            />
        </Paper>
    );
};
export default HotelOpsTab;